import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewRef
} from '@angular/core';
import { App } from '@df/core/app';
import { TimeoutService } from '@df/ng/timeout.service';
import { fromEvent, Observable, Subject } from 'rxjs';
import { map, mergeMap, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'custom-cursor, [customCursor]',
    template: `
        <ng-container *ngTemplateOutlet="customCursorContent" />
        @if (!isDisabled()) {
        <div class="custom-cursor" [@cursorAnimation]="cursorState" #customCursor>
            <ng-container *ngTemplateOutlet="customCursorIcon" />
        </div>
        }
    `,
    styleUrls: ['./custom-cursor.scss'],
    animations: [
        trigger('cursorAnimation', [
            state(
                'hide',
                style({
                    opacity: 0,
                    visibility: 'hidden'
                })
            ),
            state(
                'show',
                style({
                    opacity: 1
                })
            ),
            transition('hide => show', [style({ opacity: 0, visibility: 'visible' }), animate('250ms', style({ opacity: 1 }))]),
            transition('show => hide', [style({ opacity: 1 }), animate('250ms', style({ opacity: 0 }))])
        ])
    ],
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomCursorComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    protected ngUnsubscribe$ = new Subject<void>();
    protected move$!: Observable<Event>;
    protected enter$!: Observable<Event>;
    protected leave$!: Observable<Event>;

    cursorState: 'show' | 'hide' = 'hide';

    @ViewChild('customCursor', { read: ElementRef })
    cursor?: ElementRef;

    @ContentChild('customCursorContent')
    customCursorContent?: TemplateRef<any>;

    @ContentChild('customCursorIcon')
    customCursorIcon?: TemplateRef<any>;

    @Input()
    @HostBinding('class.is-disabled')
    disabled = false;

    @Output()
    clicked = new EventEmitter<MouseEvent>();

    @HostListener('click', ['$event'])
    onClick(event: MouseEvent): void {
        this.clicked.next(event);
    }

    set visible(value: boolean) {
        this.cursorState = value ? 'show' : 'hide';
    }

    isDisabled(): boolean {
        return this.disabled || !this.app.isPlatformBrowser;
    }

    constructor(
        protected elementRef: ElementRef,
        protected ngZone: NgZone,
        protected timeoutService: TimeoutService,
        protected changeDetectorRef: ChangeDetectorRef,
        private app: App
    ) {}

    ngOnInit(): void {
        if (this.app.isPlatformBrowser) {
            this.ngZone.runOutsideAngular(() => {
                this.enter$ = fromEvent(this.elementRef.nativeElement, 'mouseenter');
                this.leave$ = fromEvent(this.elementRef.nativeElement, 'mouseleave');
                this.move$ = fromEvent(document, 'mousemove');

                this.enter$
                    .pipe(
                        map(() => !this.isDisabled()),
                        tap(() => {
                            this.visible = true;
                            this.detectChanges();
                        }),
                        mergeMap(_ =>
                            this.move$.pipe(
                                map((e: any) => ({
                                    x: e.clientX,
                                    y: e.clientY
                                })),
                                // complete inner observable on mouseleave event
                                takeUntil(this.leave$)
                            )
                        ),
                        takeUntil(this.ngUnsubscribe$)
                    )
                    .subscribe(({ x, y }) => this.move(x, y));

                this.leave$
                    .pipe(
                        tap(() => {
                            this.visible = false;
                            this.detectChanges();
                        }),
                        takeUntil(this.ngUnsubscribe$)
                    )
                    .subscribe();
            });
        }
    }

    ngAfterViewInit(): void {
        this.detectChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['disabled']) {
            this.detectChanges();
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    protected move(left: number, top: number): void {
        if (!this.cursor) {
            return;
        }

        const elementRect = this.elementRef.nativeElement.getBoundingClientRect();

        this.cursor.nativeElement.style.left = `${left - elementRect.left}px`;
        this.cursor.nativeElement.style.top = `${top - elementRect.top}px`;
    }

    protected detectChanges(): void {
        this.timeoutService.async(() => {
            if (!(<ViewRef>this.changeDetectorRef).destroyed) {
                this.changeDetectorRef.detectChanges();
            }
        });
    }
}
